import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { keepPreviousData, useMutation, useQuery } from "@tanstack/react-query";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { addDays } from "date-fns";
import { toast } from "sonner";
import { ArrowLeft, Check, Clock, Printer, CalendarPlus, RefreshCw, MessageCircle, Phone, Trash2, Plus, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DatePriceCalendar } from "@/components/booking/DatePriceCalendar";
import { GuestPicker } from "@/components/booking/GuestPicker";
import { BookingStepper } from "@/components/booking/BookingStepper";
import { BookingRoomCard, SuggestionStrip } from "@/components/booking/RoomCard";
import { DesktopSummary, MobileSummaryBar, NightlyBreakdown } from "@/components/booking/BookingSummary";
import { useIsMobile } from "@/components/booking/BookingBar";
import { Container, Chip } from "@/components/common/Primitives";
import { useConcierge } from "@/components/concierge/ConciergeContext";
import { api, errorCode } from "@/lib/api";
import { useLang, errorText } from "@/lib/i18n";
import { useContent } from "@/lib/content";
import { useSearch } from "@/lib/search";
import { usePageMeta } from "@/lib/seo";
import { fmtDate, guestsLabel, nightsBetween, nightsLabel, nok, toISO } from "@/lib/format";
import {
  ARRIVALS, BADGE_LABELS, WISHES, allocateGuests, buildBookingUrl, cartCapacity, cartCounts, decodeCart, downloadIcs, encodeCart,
  loadLastBooking, sanitizeCart, saveLastBooking, setCartQty, suggestCombos,
} from "@/lib/booking";

const int = (v, d) => { const x = parseInt(v, 10); return Number.isFinite(x) ? x : d; };
const DRAFT_KEY = "sfh_booking_draft";
const loadDraft = () => { try { return JSON.parse(sessionStorage.getItem(DRAFT_KEY) || "null") || {}; } catch (e) { return {}; } };
const EASE = [0.2, 0.8, 0.2, 1];
const FILTERS = ["sea_view", "dog", "step_free", "family", "best_value"];
const HOTEL_IMAGE = "/images/hotel-aerial.jpg";
const selectCls = "h-12 rounded-xl border-input bg-card px-4 text-base shadow-none hover:border-foreground focus:ring-1 focus:ring-foreground";

/* ------------------------------------------------------------------ helpers */

function nextDow(from, dow, strictlyAfter = true) {
  const d = new Date(from);
  let add = (dow - d.getDay() + 7) % 7;
  if (add === 0 && strictlyAfter) add = 7;
  return addDays(d, add);
}

function quickPicks(today) {
  const fri = nextDow(today, 5);
  const mon = nextDow(today, 1);
  const thu = nextDow(today, 4);
  const sat = nextDow(today, 6);
  return [
    { id: "next-weekend", nn: "Neste helg", en: "Next weekend", ci: toISO(fri), co: toISO(addDays(fri, 2)) },
    { id: "midweek-3", nn: "3 netter midtveke", en: "3 midweek nights", ci: toISO(mon), co: toISO(addDays(mon, 3)) },
    { id: "komle-thursday", nn: "Komle-torsdag", en: "Komle Thursday", ci: toISO(thu), co: toISO(addDays(thu, 1)) },
    { id: "one-week", nn: "Ei veke", en: "One week", ci: toISO(sat), co: toISO(addDays(sat, 7)) },
  ];
}

/* ------------------------------------------------------------------ small parts */

const Section = ({ title, sub, children, testid, first = false, aside }) => (
  <section className={first ? "" : "border-t border-border pt-10"} data-testid={testid}>
    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
      <div>
        <h2 className="text-2xl font-bold leading-tight tracking-[-0.02em] text-foreground sm:text-[1.75rem]">{title}</h2>
        {sub && <p className="mt-2 max-w-2xl text-[0.9375rem] leading-relaxed text-muted-foreground">{sub}</p>}
      </div>
      {aside}
    </div>
    <div className="mt-6">{children}</div>
  </section>
);

const ContextStrip = ({ ci, co, adults, children, dog, onEdit }) => {
  const { lang, tx } = useLang();
  const n = nightsBetween(ci, co);
  return (
    <p className="text-[0.9375rem] text-muted-foreground tabular" data-testid="booking-context-strip">
      <span className="font-medium text-foreground">{fmtDate(ci, lang)} – {fmtDate(co, lang)}</span> · {nightsLabel(n, lang)} · {guestsLabel(adults + children, lang)}{children > 0 && ` (${adults} + ${children})`}{dog && ` · ${tx("hund", "dog")}`}
      {" "}<button type="button" onClick={onEdit} className="font-semibold text-foreground underline underline-offset-4" data-testid="booking-context-edit">{tx("Endre", "Change")}</button>
    </p>
  );
};

const Field = ({ id, label, error, hint, children }) => (
  <div className="space-y-1.5">
    <Label htmlFor={id} className="text-sm font-medium text-foreground">{label}</Label>
    {children}
    {error ? <p className="text-sm text-destructive" data-testid={`${id}-error`}>{error}</p> : hint ? <p className="text-sm text-muted-foreground">{hint}</p> : null}
  </div>
);

const inputCls = (err) => (err ? "border-destructive focus-visible:border-destructive focus-visible:ring-destructive" : "");

/* ------------------------------------------------------------------ step 1 */

const StepDates = ({ ci, co, adults, children, dog, party, onChange, isMobile }) => {
  const { lang, tx } = useLang();
  const picks = useMemo(() => quickPicks(new Date()), []);
  const activePick = picks.find((p) => p.ci === ci && p.co === co)?.id;
  return (
    <div data-testid="booking-step-1" className="space-y-8">
      <Section first title={tx("Når vil du kome?", "When would you like to come?")} sub={tx("Lågaste pris per natt står under kvar dato. Vel innsjekk først, så utsjekk.", "The lowest nightly rate sits under each date. Pick check-in first, then check-out.")}>
        <div className="rounded-[24px] border border-border bg-card p-4 sm:p-6">
          <DatePriceCalendar ci={ci} co={co} guests={party} months={isMobile ? 1 : 2} onChange={onChange} testid="booking-step1-calendar" />
        </div>
        <div className="mt-6">
          <p className="text-sm font-semibold text-foreground">{tx("Hurtigval", "Quick picks")}</p>
          <div className="no-scrollbar -mx-5 mt-3 flex gap-2 overflow-x-auto px-5 sm:mx-0 sm:flex-wrap sm:px-0" data-testid="booking-quick-picks">
            {picks.map((p) => (
              <Chip key={p.id} active={activePick === p.id} onClick={() => onChange({ ci: p.ci, co: p.co })} testid={`booking-quick-pick-${p.id}`}>
                {lang === "en" ? p.en : p.nn}
                <span className={`tabular ${activePick === p.id ? "text-background/70" : "text-muted-foreground"}`}>{fmtDate(p.ci, lang, "d. MMM")}–{fmtDate(p.co, lang, "d. MMM")}</span>
              </Chip>
            ))}
          </div>
        </div>
      </Section>

      <Section title={tx("Kven kjem?", "Who's coming?")} testid="booking-party">
        <div className="rounded-[24px] border border-border bg-card px-5 sm:px-6">
          <GuestPicker adults={adults} children={children} dog={dog} onChange={onChange} testid="booking-step1-guests" />
        </div>
        <p className="mt-3 text-sm text-muted-foreground">{tx("Born tel som gjest i rommet. Maks 30 netter per bestilling – større grupper ringjer 917 03 536.", "Children count as guests in the room. Max 30 nights per booking – larger groups call +47 917 03 536.")}</p>
      </Section>
    </div>
  );
};

/* ------------------------------------------------------------------ step 2 */

const StepRooms = ({ roomTypes, buildings, byId, avail, cart, party, dog, nights, onQty, onPickCombo, onEdit, ctx }) => {
  const { lang, tx, tr } = useLang();
  const [filters, setFilters] = useState([]);
  const [sort, setSort] = useState("recommended");
  const avById = useMemo(() => Object.fromEntries((avail.data?.room_types || []).map((a) => [a.room_type_id, a])), [avail.data]);
  const counts = cartCounts(cart);
  const combos = useMemo(() => (avail.data ? suggestCombos(roomTypes, avail.data, party, dog) : []), [avail.data, roomTypes, party, dog]);
  const showCombos = combos.length > 0 && (party >= 3 || combos.length >= 2);
  const activeCombo = combos.find((c) => encodeCart(c.items) === encodeCart(cart))?.key;
  const byBuilding = Object.fromEntries(buildings.map((b) => [b.id, b]));

  const price = (rt) => avById[rt.id]?.quotes?.[String(Math.max(1, Math.min(party, rt.max_guests)))]?.subtotal ?? Infinity;
  const list = useMemo(() => {
    let l = roomTypes.filter((rt) => filters.every((f) => (rt.badges || []).includes(f)));
    const score = (rt) => {
      const a = avById[rt.id]?.available ?? 0;
      let s = 0;
      if (a <= 0) s += 1000;
      if (rt.max_guests < party) s += 100;
      if (dog && !rt.dogs_allowed) s += 50;
      return s + price(rt) / 10000;
    };
    if (sort === "price_asc") l = [...l].sort((x, y) => price(x) - price(y));
    else if (sort === "price_desc") l = [...l].sort((x, y) => price(y) - price(x));
    else l = [...l].sort((x, y) => score(x) - score(y) || (x.sort || 0) - (y.sort || 0));
    return l;
  }, [roomTypes, filters, sort, avById, party, dog]); // eslint-disable-line react-hooks/exhaustive-deps

  const grouped = sort === "recommended"
    ? buildings.map((b) => ({ b, rooms: list.filter((r) => r.building === b.id) })).filter((g) => g.rooms.length)
    : [{ b: null, rooms: list }];
  const recommendedId = combos[0]?.items?.[0]?.room_type_id;
  const capacity = cartCapacity(cart, byId);
  const allSold = avail.data && roomTypes.every((rt) => (avById[rt.id]?.available ?? 0) <= 0);

  return (
    <div data-testid="booking-step-2" className="space-y-8">
      <Section first title={tx("Vel rom", "Choose your rooms")} sub={<ContextStrip {...ctx} onEdit={onEdit} />}>
        {avail.isError && (
          <div className="rounded-2xl border border-border p-6" data-testid="booking-availability-error">
            <p className="text-foreground">{errorText(errorCode(avail.error), lang)}</p>
            <Button variant="outline" className="mt-4" onClick={() => avail.refetch()} data-testid="booking-availability-retry"><RefreshCw className="h-4 w-4" /> {tx("Prøv igjen", "Try again")}</Button>
          </div>
        )}

        {showCombos && <div className="mb-6"><SuggestionStrip combos={combos} byId={byId} nights={nights} party={party} onPick={onPickCombo} activeKey={activeCombo} /></div>}

        <div className="flex flex-wrap items-center gap-2" data-testid="booking-filters">
          <div className="no-scrollbar -mx-5 flex gap-2 overflow-x-auto px-5 sm:mx-0 sm:flex-wrap sm:px-0">
            {FILTERS.map((f) => {
              const on = filters.includes(f);
              return <Chip key={f} active={on} onClick={() => setFilters((cur) => (on ? cur.filter((x) => x !== f) : [...cur, f]))} testid={`booking-filter-${f}`}>{tr(BADGE_LABELS[f])}</Chip>;
            })}
          </div>
          <div className="ml-auto">
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger className="h-10 w-[170px] rounded-full border-border bg-card text-sm shadow-none" data-testid="booking-sort"><SelectValue /></SelectTrigger>
              <SelectContent className="rounded-xl">
                <SelectItem value="recommended">{tx("Anbefalt", "Recommended")}</SelectItem>
                <SelectItem value="price_asc">{tx("Lågast pris", "Lowest price")}</SelectItem>
                <SelectItem value="price_desc">{tx("Høgast pris", "Highest price")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {avail.isLoading ? (
          <div className="mt-6 space-y-4" data-testid="booking-rooms-loading">
            {[0, 1, 2].map((i) => <div key={i} className="grid grid-cols-1 overflow-hidden rounded-2xl border border-border sm:grid-cols-[240px_1fr]"><Skeleton className="aspect-[4/3] rounded-none sm:aspect-auto sm:h-56" /><div className="space-y-3 p-6"><Skeleton className="h-6 w-2/3" /><Skeleton className="h-4 w-1/2" /><Skeleton className="h-4 w-full" /><Skeleton className="mt-6 h-10 w-1/3" /></div></div>)}
          </div>
        ) : (
          <div className="mt-6 space-y-10" data-testid="booking-room-list">
            {grouped.map(({ b, rooms }) => (
              <div key={b?.id || "all"}>
                {b && grouped.length > 1 && (
                  <div className="mb-4">
                    <h3 className="text-lg font-semibold tracking-tight text-foreground">{tr(b.name)}</h3>
                    <p className="text-sm text-muted-foreground">{tr(b.tagline)}</p>
                  </div>
                )}
                <div className="space-y-4">
                  {rooms.map((rt) => (
                    <BookingRoomCard key={rt.id} rt={rt} av={avById[rt.id]} party={party} nights={nights} qty={counts[rt.id] || 0} onQty={(q) => onQty(rt, q)} recommended={rt.id === recommendedId && party <= rt.max_guests} building={b ? null : byBuilding[rt.building]?.name} />
                  ))}
                </div>
              </div>
            ))}
            {list.length === 0 && <p className="rounded-2xl border border-dashed border-border p-8 text-center text-muted-foreground" data-testid="booking-rooms-empty">{tx("Ingen rom passar filtera. Fjern eit filter.", "No rooms match the filters. Remove a filter.")}</p>}
          </div>
        )}

        {allSold && (
          <div className="mt-6 rounded-2xl bg-secondary p-6" data-testid="booking-all-soldout">
            <p className="text-lg font-semibold text-foreground">{tx("Diverre fullt desse datoane.", "Unfortunately we're full for these dates.")}</p>
            <p className="mt-1 text-sm text-muted-foreground">{tx("Prøv å flytte opphaldet ein dag eller to – kalenderen viser kva netter som er ledige.", "Try shifting your stay by a day or two – the calendar shows which nights are open.")}</p>
            <Button variant="outline" className="mt-4" onClick={onEdit} data-testid="booking-all-soldout-change">{tx("Endre datoar", "Change dates")}</Button>
          </div>
        )}

        {cart.length > 0 && (
          <p className="mt-6 text-sm text-muted-foreground" data-testid="booking-step2-footer">
            <span className="font-semibold text-foreground">{cart.length} {tx("rom", cart.length === 1 ? "room" : "rooms")}</span> · {tx(`plass til ${capacity} gjester`, `sleeps ${capacity} guests`)}
            {capacity < party && <span className="ml-2 text-warning">· {tx(`de er ${party}`, `you are ${party}`)}</span>}
          </p>
        )}
      </Section>
    </div>
  );
};

/* ------------------------------------------------------------------ step 3 */

const StepCustomise = ({ cart, byId, quote, details, setDetails, onCartChange, onAddRoom, onBack, ctx, onEdit }) => {
  const { lang, tx, tr } = useLang();
  const aligned = quote && quote.rooms.length === cart.length;
  return (
    <div data-testid="booking-step-3" className="space-y-8">
      <Section first title={tx("Romma dine", "Your rooms")} sub={<ContextStrip {...ctx} onEdit={onEdit} />}>
        <div className="space-y-4" data-testid="booking-cart-list">
          {cart.map((item, i) => {
            const rt = byId[item.room_type_id];
            if (!rt) return null;
            const q = aligned ? quote.rooms[i] : null;
            const extra = Math.max(0, item.guests - rt.base_guests);
            return (
              <div key={`${item.room_type_id}-${i}`} className="rounded-2xl border border-border bg-card p-5 sm:p-6" data-testid={`booking-cart-item-${i}`}>
                <div className="flex items-start gap-4">
                  <img src={rt.images?.[0]} alt="" className="h-[72px] w-24 shrink-0 rounded-lg object-cover" />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0">
                        <p className="text-xs text-muted-foreground">{tx("Rom", "Room")} {i + 1}</p>
                        <h3 className="mt-0.5 text-lg font-semibold leading-snug tracking-tight text-foreground">{tr(rt.name)}</h3>
                      </div>
                      <p className="shrink-0 text-lg font-semibold tabular text-foreground" data-testid={`booking-cart-item-${i}-price`}>{q ? nok(q.subtotal, lang) : "–"}</p>
                    </div>
                    <button type="button" onClick={() => onCartChange(cart.filter((_, k) => k !== i))} className="mt-1 inline-flex items-center gap-1 text-sm text-muted-foreground underline underline-offset-4 hover:text-destructive" data-testid={`booking-cart-item-${i}-remove`}><Trash2 className="h-3.5 w-3.5" /> {tx("Fjern", "Remove")}</button>
                  </div>
                </div>
                <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label className="text-sm font-medium text-foreground">{tx("Gjester i rommet", "Guests in this room")}</Label>
                    <Select value={String(item.guests)} onValueChange={(v) => onCartChange(cart.map((c, k) => (k === i ? { ...c, guests: parseInt(v, 10) } : c)))}>
                      <SelectTrigger className={selectCls} data-testid={`booking-cart-item-${i}-guests`}><SelectValue /></SelectTrigger>
                      <SelectContent className="rounded-xl">
                        {Array.from({ length: rt.max_guests }, (_, k) => k + 1).map((g) => (
                          <SelectItem key={g} value={String(g)}>{g} {g === 1 ? tx("person", "guest") : tx("personar", "guests")}{g > rt.base_guests ? ` · +${g - rt.base_guests} ${tx("ekstraseng", "extra bed")}` : ""}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {extra > 0 && <p className="text-sm text-muted-foreground">{tx(`${extra} ekstraseng à ${nok(rt.extra_bed_price, lang)} per natt`, `${extra} extra bed at ${nok(rt.extra_bed_price, lang)} per night`)}</p>}
                  </div>
                  {(rt.board_options || []).length > 0 && (
                    <div className="space-y-1.5">
                      <Label className="text-sm font-medium text-foreground">{tx("Måltid", "Meals")}</Label>
                      <Select value={item.board || "standard"} onValueChange={(v) => onCartChange(cart.map((c, k) => (k === i ? { ...c, board: v } : c)))}>
                        <SelectTrigger className={selectCls} data-testid={`booking-cart-item-${i}-board`}><SelectValue /></SelectTrigger>
                        <SelectContent className="rounded-xl">
                          <SelectItem value="standard">{tx("Rom og frukost", "Room & breakfast")}</SelectItem>
                          {rt.board_options.map((bo) => <SelectItem key={bo.id} value={bo.id}>{tr(bo.name)} · {nok(bo.weekday, lang)}/{tx("natt", "night")}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        <Button variant="soft" className="mt-4" onClick={onAddRoom} data-testid="booking-add-room"><Plus className="h-4 w-4" /> {tx("Legg til eit rom til", "Add another room")}</Button>
        {quote && quote.problems?.length > 0 && (
          <div className="mt-4 rounded-xl bg-secondary px-4 py-3 text-sm text-warning" data-testid="booking-step3-problems">
            {quote.problems.map((p, i) => <p key={i}>{errorText(p.code, lang)}{p.room_type_id && byId[p.room_type_id] ? ` – ${tr(byId[p.room_type_id].name)}` : ""}</p>)}
          </div>
        )}
      </Section>

      <Section title={tx("Ønske for opphaldet", "Wishes for your stay")} sub={tx("Valfritt. Me gjer det me kan for å ordne det.", "Optional. We'll do what we can to arrange it.")} testid="booking-wishes-section">
        <div className="flex flex-wrap gap-2" data-testid="booking-wishes">
          {WISHES.map((w) => {
            const on = details.wishes.includes(w.id);
            return (
              <Chip key={w.id} active={on} onClick={() => setDetails({ ...details, wishes: on ? details.wishes.filter((x) => x !== w.id) : [...details.wishes, w.id] })} testid={`booking-wish-${w.id}`}>
                {on && <Check className="h-3.5 w-3.5" />}{lang === "en" ? w.en : w.nn}
              </Chip>
            );
          })}
        </div>
        <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label className="text-sm font-medium text-foreground">{tx("Omtrent når kjem de?", "Roughly when will you arrive?")}</Label>
            <Select value={details.arrival_time || "none"} onValueChange={(v) => setDetails({ ...details, arrival_time: v === "none" ? "" : v })}>
              <SelectTrigger className={selectCls} data-testid="booking-arrival-time"><Clock className="mr-2 h-4 w-4 text-muted-foreground" /><SelectValue /></SelectTrigger>
              <SelectContent className="rounded-xl">
                <SelectItem value="none">{tx("Veit ikkje enno", "Not sure yet")}</SelectItem>
                {ARRIVALS.map((a) => <SelectItem key={a} value={a}>{a === "22+" ? tx("Etter kl. 22", "After 22:00") : `${tx("kl.", "")} ${a}`.trim()}</SelectItem>)}
              </SelectContent>
            </Select>
            <p className="text-sm text-muted-foreground">{tx("Innsjekk frå kl. 15. Kjem de seint, held me rommet.", "Check-in from 15:00. Arriving late? We'll keep the room.")}</p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="booking-notes" className="text-sm font-medium text-foreground">{tx("Noko meir me bør vite?", "Anything else we should know?")}</Label>
            <Textarea id="booking-notes" value={details.notes} onChange={(e) => setDetails({ ...details, notes: e.target.value.slice(0, 1500) })} rows={3} className="rounded-xl border-input bg-card px-4 py-3 text-base hover:border-foreground focus-visible:border-foreground focus-visible:ring-1 focus-visible:ring-foreground" placeholder={tx("Allergiar, feiring, ønske om bord …", "Allergies, celebrations, table requests …")} data-testid="booking-notes" />
          </div>
        </div>
      </Section>

      <div className="border-t border-border pt-6">
        <Button variant="ghost" onClick={onBack} className="-ml-3" data-testid="booking-step3-back"><ArrowLeft className="h-4 w-4" /> {tx("Tilbake til rom", "Back to rooms")}</Button>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ step 4 */

const emptyGuest = { first_name: "", last_name: "", email: "", phone: "", invoice_company: false, company: "", org_nr: "", accept_terms: false, website: "" };

const StepGuest = ({ quote, guest, setGuest, onSubmit, onBack, ctx, onEdit }) => {
  const { tx } = useLang();
  const [touched, setTouched] = useState({});
  const errors = useMemo(() => {
    const e = {};
    if (!guest.first_name.trim()) e.first_name = tx("Skriv fornamn", "Enter your first name");
    if (!guest.last_name.trim()) e.last_name = tx("Skriv etternamn", "Enter your last name");
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(guest.email.trim())) e.email = tx("Skriv ei gyldig e-postadresse, t.d. namn@epost.no", "Enter a valid email, e.g. name@email.com");
    if (!/^[+0-9 ()-]{5,30}$/.test(guest.phone.trim())) e.phone = tx("Skriv telefonnummer som +47 900 00 000", "Enter a phone number like +47 900 00 000");
    if (guest.invoice_company && !guest.company.trim()) e.company = tx("Skriv firmanamn", "Enter company name");
    if (!guest.accept_terms) e.accept_terms = tx("Du må godta vilkåra for å sende bestillinga.", "Please accept the terms to send your booking.");
    return e;
  }, [guest, tx]);
  const show = (k) => (touched[k] || touched.__all) && errors[k];
  const submit = (e) => {
    e.preventDefault();
    setTouched({ __all: true });
    if (Object.keys(errors).length) {
      document.querySelector('[data-testid$="-error"]')?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    onSubmit();
  };
  const set = (k, v) => setGuest({ ...guest, [k]: v });
  const blur = (k) => setTouched((t) => ({ ...t, [k]: true }));
  return (
    <div data-testid="booking-step-4" className="space-y-8">
      <form id="guest-form" onSubmit={submit} noValidate className="space-y-8">
        <Section first title={tx("Kven bestiller?", "Who is booking?")} sub={<ContextStrip {...ctx} onEdit={onEdit} />}>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <Field id="guest-first-name" label={tx("Fornamn", "First name")} error={show("first_name")}>
              <Input id="guest-first-name" autoComplete="given-name" value={guest.first_name} onChange={(e) => set("first_name", e.target.value)} onBlur={() => blur("first_name")} className={inputCls(show("first_name"))} data-testid="guest-first-name" />
            </Field>
            <Field id="guest-last-name" label={tx("Etternamn", "Last name")} error={show("last_name")}>
              <Input id="guest-last-name" autoComplete="family-name" value={guest.last_name} onChange={(e) => set("last_name", e.target.value)} onBlur={() => blur("last_name")} className={inputCls(show("last_name"))} data-testid="guest-last-name" />
            </Field>
            <Field id="guest-email" label={tx("E-post", "Email")} error={show("email")} hint={tx("Stadfestinga kjem hit.", "Your confirmation goes here.")}>
              <Input id="guest-email" type="email" inputMode="email" autoComplete="email" value={guest.email} onChange={(e) => set("email", e.target.value)} onBlur={() => blur("email")} className={inputCls(show("email"))} data-testid="guest-email" />
            </Field>
            <Field id="guest-phone" label={tx("Telefon", "Phone")} error={show("phone")} hint={tx("T.d. +47 900 00 000", "E.g. +47 900 00 000")}>
              <Input id="guest-phone" type="tel" inputMode="tel" autoComplete="tel" value={guest.phone} onChange={(e) => set("phone", e.target.value)} onBlur={() => blur("phone")} className={inputCls(show("phone"))} data-testid="guest-phone" />
            </Field>
          </div>
          <input type="text" name="website" value={guest.website} onChange={(e) => set("website", e.target.value)} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />

          <div className="mt-5 rounded-2xl border border-border bg-card p-5">
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-[0.9375rem] font-semibold text-foreground">{tx("Bestiller for firma", "Booking for a company")}</p>
                <p className="text-sm text-muted-foreground">{tx("Me set firmanamn og org.nr på kvitteringa.", "We'll put the company name and org. no. on the receipt.")}</p>
              </div>
              <Switch checked={guest.invoice_company} onCheckedChange={(v) => set("invoice_company", v)} data-testid="guest-company-toggle" aria-label={tx("Firma", "Company")} />
            </div>
            <AnimatePresence initial={false}>
              {guest.invoice_company && (
                <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.25, ease: EASE }} className="overflow-hidden">
                  <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2" data-testid="guest-company-fields">
                    <Field id="guest-company" label={tx("Firmanamn", "Company name")} error={show("company")}>
                      <Input id="guest-company" autoComplete="organization" value={guest.company} onChange={(e) => set("company", e.target.value)} onBlur={() => blur("company")} className={inputCls(show("company"))} data-testid="guest-company" />
                    </Field>
                    <Field id="guest-org-nr" label={tx("Org.nr (valfritt)", "Org. no. (optional)")}>
                      <Input id="guest-org-nr" inputMode="numeric" value={guest.org_nr} onChange={(e) => set("org_nr", e.target.value)} data-testid="guest-org-nr" />
                    </Field>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </Section>

        <Section title={tx("Avbestilling og betaling", "Cancellation and payment")}>
          <ul className="divide-y divide-border rounded-2xl border border-border bg-card px-5" data-testid="booking-promise">
            <li className="flex items-start justify-between gap-6 py-4 text-[0.9375rem]"><span className="text-muted-foreground">{tx("Avbestilling", "Cancellation")}</span><span className="text-right font-medium text-foreground">{quote?.cancel_deadline_display ? tx(`Gratis fram til ${quote.cancel_deadline_display.nn}`, `Free until ${quote.cancel_deadline_display.en}`) : tx("Gratis fram til kl. 18 dagen før", "Free until 18:00 the day before")}</span></li>
            <li className="flex items-start justify-between gap-6 py-4 text-[0.9375rem]"><span className="text-muted-foreground">{tx("Betaling", "Payment")}</span><span className="text-right font-medium text-foreground">{tx("På hotellet – kort eller Vipps", "At the hotel – card or Vipps")}</span></li>
            <li className="flex items-start justify-between gap-6 py-4 text-[0.9375rem]"><span className="text-muted-foreground">{tx("Gebyr", "Fees")}</span><span className="text-right font-medium text-foreground">{tx("Ingen skjulte gebyr", "No hidden fees")}</span></li>
          </ul>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{tx("Seinare avbestilling eller manglande oppmøte vert fakturert for opphaldet. Bestillinga vert stadfesta av hotellet på e-post.", "Later cancellation or no-show is charged for the stay. The hotel confirms your booking by email.")}</p>
          <label className={`mt-5 flex cursor-pointer items-start gap-3 rounded-2xl border bg-card p-4 ${show("accept_terms") ? "border-destructive" : "border-border"}`} htmlFor="guest-terms">
            <Checkbox id="guest-terms" checked={guest.accept_terms} onCheckedChange={(v) => { set("accept_terms", !!v); blur("accept_terms"); }} className="mt-0.5 h-5 w-5 rounded-md" data-testid="guest-accept-terms" />
            <span className="text-[0.9375rem] text-foreground">{tx("Eg godtar ", "I accept the ")}<Link to="/praktisk#vilkar" target="_blank" className="font-semibold underline underline-offset-4" data-testid="booking-terms-link">{tx("vilkåra for bestilling og avbestilling", "booking and cancellation terms")}</Link>.</span>
          </label>
          {show("accept_terms") && <p className="mt-2 text-sm text-destructive" data-testid="guest-terms-error">{errors.accept_terms}</p>}
        </Section>

        <div className="flex items-center justify-between border-t border-border pt-6">
          <Button type="button" variant="ghost" onClick={onBack} className="-ml-3" data-testid="booking-step4-back"><ArrowLeft className="h-4 w-4" /> {tx("Tilbake", "Back")}</Button>
          <p className="text-sm text-muted-foreground">{tx("Me lagrar berre det som trengst for opphaldet.", "We only store what's needed for your stay.")}</p>
        </div>
      </form>
    </div>
  );
};

/* ------------------------------------------------------------------ confirmation */

const STATUS = {
  pending: { nn: "Motteken – ventar på stadfesting", en: "Received – awaiting confirmation", cls: "bg-accent text-accent-foreground" },
  confirmed: { nn: "Stadfesta", en: "Confirmed", cls: "bg-success text-white" },
  declined: { nn: "Avslått", en: "Declined", cls: "bg-destructive text-destructive-foreground" },
  cancelled: { nn: "Avbestilt", en: "Cancelled", cls: "bg-muted text-muted-foreground" },
};

export const StatusPill = ({ status, lang, testid }) => {
  const st = STATUS[status] || STATUS.pending;
  return <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${st.cls}`} data-testid={testid}>{lang === "en" ? st.en : st.nn}</span>;
};

export const BookingDetails = ({ b, byId, lang }) => {
  const { tx, tr } = useLang();
  return (
    <div className="space-y-6" data-testid="booking-details">
      <div className="grid grid-cols-2 gap-5 sm:grid-cols-3">
        <div><p className="text-sm text-muted-foreground">{tx("Innsjekk", "Check-in")}</p><p className="mt-1 text-lg font-semibold leading-tight tabular text-foreground">{fmtDate(b.check_in, lang, lang === "en" ? "EEE d MMM" : "EEE d. MMM")}</p><p className="text-sm text-muted-foreground">{tx("frå 15.00", "from 15:00")}</p></div>
        <div><p className="text-sm text-muted-foreground">{tx("Utsjekk", "Check-out")}</p><p className="mt-1 text-lg font-semibold leading-tight tabular text-foreground">{fmtDate(b.check_out, lang, lang === "en" ? "EEE d MMM" : "EEE d. MMM")}</p><p className="text-sm text-muted-foreground">{tx("før 12.00", "before 12:00")}</p></div>
        <div className="col-span-2 sm:col-span-1"><p className="text-sm text-muted-foreground">{tx("Opphald", "Stay")}</p><p className="mt-1 text-lg font-semibold leading-tight text-foreground">{nightsLabel(b.night_count, lang)}</p><p className="text-sm text-muted-foreground">{guestsLabel(b.guests_total, lang)}{b.dog ? ` · ${tx("hund", "dog")}` : ""}</p></div>
      </div>
      <ul className="divide-y divide-border border-y border-border" data-testid="booking-details-rooms">
        {b.rooms.map((r, i) => (
          <li key={i} className="flex items-center gap-4 py-3.5">
            {(r.image || byId?.[r.room_type_id]?.images?.[0]) && <img src={r.image || byId[r.room_type_id].images[0]} alt="" className="h-14 w-[72px] shrink-0 rounded-lg object-cover" />}
            <div className="min-w-0 flex-1">
              <p className="text-[0.9375rem] font-medium text-foreground">{tr(r.name)}</p>
              <p className="text-sm text-muted-foreground">{guestsLabel(r.guests, lang)}{r.extra_beds > 0 && ` · ${r.extra_beds} ${tx("ekstraseng", "extra bed")}`}{r.board !== "standard" && r.board_name && ` · ${tr(r.board_name)}`}</p>
            </div>
            <p className="text-[0.9375rem] font-medium tabular text-foreground">{nok(r.subtotal, lang)}</p>
          </li>
        ))}
      </ul>
      <div className="flex items-baseline justify-between">
        <span className="text-base font-semibold text-foreground">{tx("Totalt – betalast på hotellet", "Total – payable at the hotel")}</span>
        <span className="text-xl font-semibold tabular text-foreground" data-testid="booking-details-total">{nok(b.total, lang)}</span>
      </div>
      <NightlyBreakdown quote={b} />
      {(b.wishes?.length > 0 || b.arrival_time || b.notes) && (
        <div className="border-t border-border pt-5 text-sm">
          <p className="font-semibold text-foreground">{tx("Ønske", "Wishes")}</p>
          <div className="mt-2 flex flex-wrap gap-2">
            {(b.wishes || []).map((w) => { const def = WISHES.find((x) => x.id === w); return <span key={w} className="rounded-full bg-secondary px-3 py-1 text-xs font-medium text-foreground">{def ? (lang === "en" ? def.en : def.nn) : w}</span>; })}
            {b.arrival_time && <span className="inline-flex items-center gap-1 rounded-full bg-secondary px-3 py-1 text-xs font-medium text-foreground"><Clock className="h-3 w-3" />{b.arrival_time === "22+" ? tx("Etter kl. 22", "After 22:00") : `${tx("kl.", "")} ${b.arrival_time}`.trim()}</span>}
          </div>
          {b.notes && <p className="mt-3 whitespace-pre-wrap text-muted-foreground">{b.notes}</p>}
        </div>
      )}
      <div className="border-t border-border pt-5 text-sm">
        <p className="font-semibold text-foreground">{tx("Kontaktperson", "Contact")}</p>
        <p className="mt-1 text-foreground">{b.guest.first_name} {b.guest.last_name}{b.guest.invoice_company && b.guest.company ? ` · ${b.guest.company}${b.guest.org_nr ? ` (${b.guest.org_nr})` : ""}` : ""}</p>
        <p className="text-muted-foreground">{b.guest.email} · {b.guest.phone}</p>
      </div>
    </div>
  );
};

const StepConfirmation = ({ booking: b, byId }) => {
  const { lang, tx } = useLang();
  const { ask } = useConcierge();
  const roomNames = Object.fromEntries(Object.values(byId).map((r) => [r.id, r.name?.[lang] || r.name?.nn]));
  return (
    <div data-testid="booking-step-5" className="mx-auto max-w-4xl pt-2 lg:pt-6">
      <div className="flex items-center gap-3">
        <span className="flex h-10 w-10 items-center justify-center rounded-full bg-success text-white"><Check className="h-5 w-5" /></span>
        <StatusPill status={b.status} lang={lang} testid="booking-confirmation-status" />
      </div>
      <h1 className="mt-5 text-3xl font-semibold leading-tight tracking-tight text-foreground sm:text-4xl">{tx("Takk, ", "Thank you, ")}{b.guest.first_name}. {tx("Me gler oss til å sjå deg.", "We look forward to seeing you.")}</h1>
      <p className="mt-3 max-w-2xl text-[0.9375rem] leading-relaxed text-muted-foreground sm:text-base">{b.status === "confirmed" ? tx("Opphaldet er stadfesta. Me har sendt deg ei stadfesting på e-post.", "Your stay is confirmed. We've sent a confirmation by email.") : tx("Me har motteke bestillinga og sender ei stadfesting på e-post så snart hotellet har sett over henne – som regel innan få timar.", "We've received your booking and will email a confirmation as soon as the hotel has reviewed it – usually within a few hours.")}</p>

      <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_300px] lg:gap-8">
        <div className="rounded-2xl border border-border bg-card p-5 sm:p-7">
          <div className="flex flex-wrap items-end justify-between gap-4 border-b border-border pb-5">
            <div>
              <p className="text-sm text-muted-foreground">{tx("Bookingreferanse", "Booking reference")}</p>
              <p className="mt-1 text-2xl font-semibold tracking-wide tabular text-foreground" data-testid="booking-confirmation-reference">{b.ref}</p>
            </div>
            <p className="text-sm text-muted-foreground">{tx(`Gratis avbestilling fram til ${b.cancel_deadline_display?.nn}`, `Free cancellation until ${b.cancel_deadline_display?.en}`)}</p>
          </div>
          <div className="pt-5"><BookingDetails b={b} byId={byId} lang={lang} /></div>
        </div>
        <div className="space-y-4 no-print">
          <div className="grid grid-cols-1 gap-2">
            <Button size="lg" className="justify-start" onClick={() => downloadIcs(b, roomNames, lang)} data-testid="booking-confirmation-ics-download"><CalendarPlus className="h-4 w-4" /> {tx("Legg i kalender", "Add to calendar")}</Button>
            <Button variant="soft" size="lg" className="justify-start" asChild data-testid="booking-confirmation-my-booking-link"><Link to={`/mi-bestilling?ref=${b.ref}&email=${encodeURIComponent(b.guest.email)}`}><ChevronRight className="h-4 w-4" /> {tx("Mi bestilling", "My booking")}</Link></Button>
            <Button variant="soft" size="lg" className="justify-start" onClick={() => window.print()} data-testid="booking-confirmation-print"><Printer className="h-4 w-4" /> {tx("Skriv ut", "Print")}</Button>
          </div>
          <button type="button" onClick={() => ask(lang === "en" ? `I have booked ${b.ref} and would like a dinner table on ${fmtDate(b.check_in, "en")}` : `Eg har bestilt ${b.ref} og vil gjerne ha bord til middag ${fmtDate(b.check_in, "nn")}`)} className="flex w-full items-start gap-3 rounded-2xl bg-secondary p-5 text-left transition-colors hover:bg-muted" data-testid="booking-confirmation-concierge">
            <MessageCircle className="mt-0.5 h-5 w-5 shrink-0 text-foreground" />
            <span><span className="block text-[0.9375rem] font-semibold text-foreground">{tx("Bord til middag?", "Table for dinner?")}</span><span className="mt-0.5 block text-sm text-muted-foreground">{tx("Spør Fjordverten om middagstider og meny.", "Ask Fjordverten about dinner times and menu.")}</span></span>
          </button>
          <div className="flex items-start gap-3 rounded-2xl border border-border p-5">
            <Phone className="mt-0.5 h-5 w-5 shrink-0 text-foreground" />
            <span><span className="block text-[0.9375rem] font-semibold text-foreground">{tx("Spørsmål?", "Questions?")}</span><span className="mt-0.5 block text-sm text-muted-foreground"><a href="tel:+4791703536" className="underline underline-offset-4 tabular">917 03 536</a><br /><a href="mailto:post@fjordhotellet.no" className="underline underline-offset-4">post@fjordhotellet.no</a></span></span>
          </div>
        </div>
      </div>
      <div className="mt-10 no-print"><Button variant="ghost" className="-ml-3" asChild><Link to="/"><ArrowLeft className="h-4 w-4" /> {tx("Tilbake til framsida", "Back to the front page")}</Link></Button></div>
    </div>
  );
};

/* ------------------------------------------------------------------ page */

export default function Booking() {
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  const { lang, tx } = useLang();
  const { data: content, isLoading: contentLoading } = useContent();
  const { search, setSearch } = useSearch();
  const isMobile = useIsMobile();
  const reduce = useReducedMotion();
  usePageMeta(tx("Bestill opphald", "Book your stay"), tx("Bestill rom direkte hos Skånevik Fjordhotel – beste pris, frukost inkludert, betal på hotellet.", "Book direct at Skånevik Fjordhotel – best rate, breakfast included, pay at the hotel."));

  const roomTypes = useMemo(() => content?.room_types || [], [content]);
  const buildings = useMemo(() => content?.buildings || [], [content]);
  const byId = useMemo(() => Object.fromEntries(roomTypes.map((r) => [r.id, r])), [roomTypes]);

  // ---- URL state (source of truth)
  const ci = sp.get("ci") || "";
  const co = sp.get("co") || "";
  const adults = Math.max(1, int(sp.get("a"), search.adults || 2));
  const children = Math.max(0, int(sp.get("c"), 0));
  const dog = sp.get("dog") === "1";
  const cartRaw = sp.get("r") || "";
  const cart = useMemo(() => sanitizeCart(decodeCart(cartRaw), byId), [cartRaw, byId]);
  const stepParam = int(sp.get("step"), 1);
  const party = adults + children;
  const nights = nightsBetween(ci, co);
  const hasDates = !!(ci && co) && nights > 0;

  const hydrated = useRef(false);
  useEffect(() => {
    if (hydrated.current) return;
    hydrated.current = true;
    if (!sp.get("ci") && search.ci && search.co && !sp.get("step")) {
      navigate(buildBookingUrl({ ...search, cart, step: 1 }), { replace: true });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setSearch(hasDates ? { ci, co, adults, children, dog } : { adults, children, dog });
  }, [ci, co, adults, children, dog, hasDates, setSearch]);

  const update = useCallback((patch, { push = false } = {}) => {
    const next = { ci, co, adults, children, dog, cart, ...patch };
    navigate(buildBookingUrl({ ...next, step: patch.step ?? stepParam }), { replace: !push });
  }, [ci, co, adults, children, dog, cart, stepParam, navigate]);

  const lastBooking = useMemo(() => loadLastBooking(), [sp]); // eslint-disable-line react-hooks/exhaustive-deps
  const confirmRef = sp.get("ref");

  let step = stepParam;
  if (step === 5) {
    if (!lastBooking || (confirmRef && lastBooking.ref !== confirmRef)) step = 1;
  } else {
    if (!hasDates) step = 1;
    else if (step >= 3 && cart.length === 0) step = 2;
    step = Math.min(Math.max(step, 1), 4);
  }
  const reachable = !hasDates ? 1 : cart.length === 0 ? 2 : 4;

  useEffect(() => { window.scrollTo({ top: 0, behavior: reduce ? "instant" : "smooth" }); }, [step, reduce]);

  // ---- data
  const avail = useQuery({
    queryKey: ["availability", ci, co, party],
    queryFn: async () => (await api.get("/availability", { params: { check_in: ci, check_out: co, guests: party } })).data,
    enabled: hasDates && step >= 2 && step <= 4,
    staleTime: 20_000,
  });
  const quoteQ = useQuery({
    queryKey: ["quote", ci, co, cartRaw, dog],
    queryFn: async () => (await api.post("/quote", { check_in: ci, check_out: co, rooms: cart, dog })).data,
    enabled: hasDates && cart.length > 0 && step >= 2 && step <= 4,
    placeholderData: keepPreviousData,
    staleTime: 20_000,
  });
  const quote = quoteQ.data;

  // ---- local details (draft in sessionStorage)
  const [details, setDetailsState] = useState(() => ({ wishes: [], arrival_time: "", notes: "", ...loadDraft().details }));
  const [guest, setGuestState] = useState(() => ({ ...emptyGuest, ...loadDraft().guest, accept_terms: false }));
  const setDetails = (d) => { setDetailsState(d); sessionStorage.setItem(DRAFT_KEY, JSON.stringify({ details: d, guest })); };
  const setGuest = (g) => { setGuestState(g); sessionStorage.setItem(DRAFT_KEY, JSON.stringify({ details, guest: { ...g, website: "" } })); };

  // ---- cart handlers
  const onQty = (rt, qty) => update({ cart: allocateGuests(setCartQty(cart, rt.id, qty, rt), party, byId) });
  const onPickCombo = (combo) => update({ cart: combo.items, step: 3 }, { push: true });
  const onCartChange = (next) => update({ cart: next, step: next.length ? 3 : 2 });

  // ---- submit
  const createBooking = useMutation({
    mutationFn: async () => (await api.post("/bookings", {
      check_in: ci, check_out: co, rooms: cart, dog, adults, children,
      guest: { first_name: guest.first_name.trim(), last_name: guest.last_name.trim(), email: guest.email.trim(), phone: guest.phone.trim(), company: guest.invoice_company ? guest.company.trim() : "", org_nr: guest.invoice_company ? guest.org_nr.trim() : "", invoice_company: guest.invoice_company },
      wishes: details.wishes, arrival_time: details.arrival_time || null, notes: details.notes, lang, accept_terms: guest.accept_terms, website: guest.website,
    })).data,
    onSuccess: (b) => {
      saveLastBooking(b);
      sessionStorage.removeItem(DRAFT_KEY);
      toast.success(tx(`Bestilling ${b.ref} er sendt.`, `Booking ${b.ref} has been sent.`));
      navigate(`/bestill?step=5&ref=${b.ref}`);
    },
    onError: (err) => {
      const code = errorCode(err);
      if (code === "quote_invalid" || code === "not_available") {
        toast.error(errorText("quote_invalid", lang));
        avail.refetch();
        quoteQ.refetch();
        update({ step: 2 });
      } else {
        toast.error(errorText(code, lang));
      }
    },
  });

  const ctx = { ci, co, adults, children, dog };
  const goEdit = () => update({ step: 1 }, { push: true });

  // ---- CTA per step (lives in the receipt on desktop, bottom bar on mobile)
  const cta = step === 1 ? { label: tx("Sjå ledige rom", "See available rooms"), disabled: !hasDates, onClick: () => update({ step: 2 }, { push: true }), hint: hasDates ? null : tx("Vel innsjekk og utsjekk i kalenderen", "Pick check-in and check-out in the calendar") }
    : step === 2 ? { label: cart.length ? tx(`Hald fram med ${cart.length} rom`, `Continue with ${cart.length} ${cart.length === 1 ? "room" : "rooms"}`) : tx("Hald fram", "Continue"), disabled: cart.length === 0, onClick: () => update({ step: 3 }, { push: true }), hint: cart.length ? null : tx("Legg til minst eitt rom", "Add at least one room") }
      : step === 3 ? { label: tx("Til gjesteinfo", "Your details"), disabled: !quote?.valid || quoteQ.isFetching, onClick: () => update({ step: 4 }, { push: true }) }
        : step === 4 ? { label: createBooking.isPending ? tx("Sender …", "Sending …") : tx("Send bestilling", "Send booking"), disabled: createBooking.isPending || !quote?.valid, type: "submit", form: "guest-form", testid: "booking-submit" }
          : null;

  const receiptImage = cart.length && byId[cart[0].room_type_id]?.images?.[0] ? byId[cart[0].room_type_id].images[0] : HOTEL_IMAGE;

  if (contentLoading && step !== 1) {
    return <Container className="py-24"><Skeleton className="h-10 w-1/3" /><Skeleton className="mt-6 h-64 w-full" /></Container>;
  }

  const summaryProps = { ci, co, adults, children, dog, cart, quote, byId, isLoading: quoteQ.isLoading, step, image: receiptImage, onEdit: goEdit };
  const variants = reduce ? { initial: { opacity: 1 }, animate: { opacity: 1 }, exit: { opacity: 1 } } : { initial: { opacity: 0, y: 8 }, animate: { opacity: 1, y: 0 }, exit: { opacity: 0, y: -6 } };

  return (
    <Container className={step === 5 ? "pb-24 pt-8" : "pb-32 pt-8 lg:pb-24 lg:pt-12"} data-testid="booking-page">
      {step < 5 && (
        <>
          <div className="flex flex-wrap items-end justify-between gap-3">
            <h1 className="text-[2rem] font-bold leading-tight tracking-[-0.03em] text-foreground lg:text-[2.5rem]">{tx("Bestill opphald", "Book your stay")}</h1>
            <p className="hidden text-sm text-muted-foreground sm:block">{tx("Betal på hotellet · Gratis avbestilling · Frukost inkludert", "Pay at the hotel · Free cancellation · Breakfast included")}</p>
          </div>
          <BookingStepper step={step} reachable={reachable} onGo={(s) => update({ step: s }, { push: true })} className="mt-5 lg:mt-8" />
        </>
      )}

      {step === 5 ? (
        <StepConfirmation booking={lastBooking} byId={byId} />
      ) : (
        <div className="mt-8 grid grid-cols-1 gap-10 lg:mt-12 lg:grid-cols-[minmax(0,1fr)_400px] lg:gap-14 xl:grid-cols-[minmax(0,1fr)_420px] xl:gap-20">
          <div className="min-w-0">
            <AnimatePresence mode="wait" initial={false}>
              <motion.div key={step} {...variants} transition={{ duration: 0.3, ease: EASE }}>
                {step === 1 && <StepDates ci={ci} co={co} adults={adults} children={children} dog={dog} party={party} isMobile={isMobile} onChange={(v) => update(v)} />}
                {step === 2 && <StepRooms roomTypes={roomTypes} buildings={buildings} byId={byId} avail={avail} cart={cart} party={party} dog={dog} nights={nights} onQty={onQty} onPickCombo={onPickCombo} onEdit={goEdit} ctx={ctx} />}
                {step === 3 && <StepCustomise cart={cart} byId={byId} quote={quote} details={details} setDetails={setDetails} onCartChange={onCartChange} onAddRoom={() => update({ step: 2 }, { push: true })} onBack={() => update({ step: 2 }, { push: true })} ctx={ctx} onEdit={goEdit} />}
                {step === 4 && <StepGuest quote={quote} guest={guest} setGuest={setGuest} onSubmit={() => createBooking.mutate()} onBack={() => update({ step: 3 }, { push: true })} ctx={ctx} onEdit={goEdit} />}
              </motion.div>
            </AnimatePresence>
          </div>
          <DesktopSummary {...summaryProps} cta={cta} />
        </div>
      )}

      {step < 5 && <MobileSummaryBar cta={cta} {...summaryProps} />}
      <div className="sr-only" aria-live="polite">{quote ? `${tx("Totalt", "Total")} ${nok(quote.total, lang)}` : ""}</div>
    </Container>
  );
}
